// src/data/appData.ts

import {
  AppData,
  FooterLink,
  ServiceCatalogGroup,
  SolutionDetailsPageData,
} from "../types/interfaces";
import alluratechEcosystemImg from "../assets/images/alluratech-hero-helsinki.png";
import alluratechCreatorsImg from "../assets/images/alluratech-creators.png";
import alluratechBuildersImg from "../assets/images/alluratech-builders.png";
import inovation_and_collaboration_img from "../assets/images/innovation_and_collaboration.png";
import discover_innoWork_img from "../assets/images/discover_innoWork.png";
import Software_Development_Solutions_img from "../assets/images/Software_Development_Solutions.png";
import itConsultancy_img from "../assets/images/IT_and_AI_Consultancy.png";
import techRecruitment_img from "../assets/images//Tech_Event_Organization.png";
import connectSolve_img from "../assets/images/ConnectSolve_Platform.png";
import marius_monkam_img from "../assets/images/marius_monkam.jpg";
import reactBusinessStarter_img from "../assets/images/react-business-starter.png";
import backendCraftWizard_img from "../assets/images/backend-craft-wizard.png";
import alluratech_cover_img from "../assets/images/alluratech-cover-matchxr.png";

const ALLURAXR_PRODUCTION_URL = "https://alluraxr.alluratech.fi";
const MOBILE_MUSIC_STORY_LAB_URL = "https://music-story.alluratech.fi";

const alluraXrPath = (path: string) => `${ALLURAXR_PRODUCTION_URL}${path}`;

const HELSINKI_DEV_JOBS_URL = "https://helsinki-dev-jobs.mariusmonk.chatgpt.site/";

const WHATSAPP_COMMUNITY_URL = "https://chat.whatsapp.com/LMrmGeE0yOCJssDt3ZqxoU";
const WHATSAPP_CHANNEL_URL = "https://whatsapp.com/channel/0029Vb9XXZNADTO5tUT8F00d";

const alluraTechSocialLinks: FooterLink[] = [
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/alluratech/",
    description: "Company updates and professional news",
  },
  {
    name: "WhatsApp",
    label: "WhatsApp community",
    href: WHATSAPP_COMMUNITY_URL,
    description: "Join the AlluraTech community",
  },
  {
    name: "WhatsApp Channel",
    label: "WhatsApp Channel",
    href: WHATSAPP_CHANNEL_URL,
    description: "Follow official AlluraTech updates",
  },
  {
    name: "Telegram",
    href: "https://t.me/+gHJ1UVllgw4wYWRk",
    description: "Community updates",
  },
  {
    name: "TikTok",
    label: "AlluraXR on TikTok",
    href: "https://www.tiktok.com/@alluraxr.by.allur",
    description: "Creative work from the AlluraXR ecosystem",
    excludeFromFooter: true,
  },
  {
    name: "Founder LinkedIn",
    href: "https://www.linkedin.com/in/mariusmonkam/",
    description: "Connect with our founder",
    excludeFromFooter: true,
  },
];

const alluraTechServiceCatalogGroups: ServiceCatalogGroup[] = [
  {
    title: "Create and share",
    description:
      "Working products people can use today to create, publish, and reach an audience.",
    items: [
      {
        title: "AlluraXR",
        description:
          "A home for immersive media, creator profiles, virtual experiences, and connected publishing tools.",
        status: "Live",
        audience: "Creators, businesses, educators, and communities.",
        outcome:
          "Create and discover media and immersive experiences in one connected platform.",
        poweredBy: "AlluraXR",
        tags: ["XR", "Media", "Creators"],
        ctaText: "Explore AlluraXR",
        ctaLink: ALLURAXR_PRODUCTION_URL,
        secondaryCtaText: "Learn more",
        secondaryCtaLink: "/solutions/alluraxr-platform",
      },
      {
        title: "Music & Story Lab",
        description:
          "A dedicated mobile-friendly workspace for creating music, stories, covers, and collections.",
        status: "Live",
        audience: "Musicians, storytellers, educators, and creators.",
        outcome:
          "Move an idea into an organized music or story draft with publishing preparation.",
        poweredBy: "AlluraXR",
        tags: ["Music", "Stories", "Creator tools"],
        ctaText: "Open Music & Story Lab",
        ctaLink: MOBILE_MUSIC_STORY_LAB_URL,
        secondaryCtaText: "Learn more",
        secondaryCtaLink: "/solutions/music-story-lab",
      },
      {
        title: "Free Video Starter",
        description:
          "Request one 15-second introductory video using material you have permission to share. Every request is reviewed before production.",
        status: "Live request",
        audience: "Creators, projects, and small organizations.",
        outcome:
          "Get a short video introduction without a payment or quote for the starter.",
        poweredBy: "AlluraXR video service",
        tags: ["Video", "Free starter", "Human review"],
        ctaText: "Request a free video",
        ctaLink: alluraXrPath("/video-marketplace"),
      },
    ],
  },
  {
    title: "Build opportunities",
    description:
      "Active pathways that connect practical projects, visible contributions, and technical roles.",
    items: [
      {
        title: "AlluraPath Finland",
        description:
          "Join or lead a project, collaborate with a team, and submit real work for AlluraTech review.",
        status: "Live onboarding",
        audience: "Developers, students, career changers, and project leads in Finland.",
        outcome:
          "Build reviewable experience and optionally show approved community recognition.",
        poweredBy: "AlluraXR accounts",
        tags: ["Projects", "Teams", "Contribution"],
        ctaText: "Join AlluraPath",
        ctaLink: alluraXrPath("/finland/allurapath/join"),
        secondaryCtaText: "Learn more",
        secondaryCtaLink: "/solutions/allurapath-finland",
      },
      {
        title: "Helsinki Dev Jobs",
        description:
          "Browse AI and tech roles and market signals. Employers can publish free listings; related roles can lead builders to AlluraPath projects.",
        status: "Live",
        audience: "Tech jobseekers, employers, and project teams in Finland.",
        outcome:
          "Connect job discovery with practical opportunities to demonstrate skills.",
        poweredBy: "Helsinki Dev Jobs",
        tags: ["Jobs", "Finland", "Skills"],
        ctaText: "Explore Helsinki Dev Jobs",
        ctaLink: HELSINKI_DEV_JOBS_URL,
      },
    ],
  },
];
const alluraTechSolutionDetails: SolutionDetailsPageData[] = [
  {
    id: "alluraxr-platform",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "AlluraXR Production Platform",
        subtitle:
          "The live AlluraTech platform for accounts, media, websites, XR experiences, and service-backed creator workflows.",
        ctaText: "Open AlluraXR",
        ctaLink: ALLURAXR_PRODUCTION_URL,
        image: Software_Development_Solutions_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>AlluraXR is the production environment where AlluraTech services become operational. It connects profiles, companies, media, websites, XR scenes, creator workflows, regional programs, and internal service delivery into one authenticated platform.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Shared Service Infrastructure",
        description:
          "Authentication, profiles, company pages, media, AI tools, service dashboards, and publishing workflows can support multiple independent services.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Production Destinations",
        description:
          "Public pages, dashboards, mobile labs, marketplaces, and XR routes all give each service a clear destination instead of hiding it inside one generic product.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Launch the AlluraXR platform",
        ctaText: "Open AlluraXR",
        ctaLink: ALLURAXR_PRODUCTION_URL,
      },
    ],
  },
  {
    id: "event-hq",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Event HQ",
        subtitle:
          "A reusable event operating space for registrations, media, updates, creators, and immersive follow-up.",
        ctaText: "Open Event Dashboard",
        ctaLink: alluraXrPath("/dashboard/events"),
        image: techRecruitment_img,
      },
      {
        type: "TextSection",
        title: "Service Model",
        id: "overview",
        content: `
          <p>Event HQ packages AlluraXR event tools into a B2B service for schools, incubators, companies, venues, and community programs. The goal is to run events once, then reuse the content, media, participant data, and immersive assets after the event.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Hybrid Event Operations",
        description:
          "Support event pages, media capture, updates, creator participation, and follow-up content from the same AlluraXR account base.",
        image: techRecruitment_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Campaign Reuse",
        description:
          "Turn event outputs into replay pages, media stories, XR rooms, creator showcases, and future campaign assets.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Plan an AlluraXR-powered event",
        ctaText: "Request Pilot",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "site-studio",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Site Studio",
        subtitle:
          "Template-led websites, company profiles, service pages, and XR-linked pages managed from AlluraXR.",
        ctaText: "Open Builder",
        ctaLink: alluraXrPath("/dashboard/website-builder"),
        image: reactBusinessStarter_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>Site Studio is the practical public-facing website service layer. React Business Starter is now treated as a legacy/internal template foundation, while Site Studio and Fullstack-Craft-Wizard carry the productized website generation and service delivery story.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Profile and Company-Aware Pages",
        description:
          "Build pages that can reuse AlluraXR profiles, company data, templates, media, and service content instead of starting from blank files.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Service Packages",
        description:
          "Offer business websites, program pages, campaign pages, and creator pages as clear packages with upgrade paths into XR and media services.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Launch a service-backed website",
        ctaText: "Request Service",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "media-studio",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Media Studio",
        subtitle:
          "A production workspace for media assets that support websites, campaigns, XR scenes, and publishing workflows.",
        ctaText: "Open Media Studio",
        ctaLink: alluraXrPath("/dashboard/media-studio"),
        image: Software_Development_Solutions_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>Media Studio lets AlluraTech turn media management into a repeatable service. Assets can support websites, events, music and story collections, XR experiences, campaigns, and creator marketplaces.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Reusable Media Collections",
        description:
          "Organize images, videos, covers, campaign assets, and service content so they can be reused across AlluraTech surfaces.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Publishing Preparation",
        description:
          "Prepare media for platform pages, social assets, creator channels, events, and immersive presentation contexts.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Build a media workflow",
        ctaText: "Request Service",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "music-story-lab",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Music & Story Lab",
        subtitle:
          "Mobile-first creation for music, stories, albums, playlists, covers, narration, and publishing preparation.",
        ctaText: "Open Mobile Lab",
        ctaLink: MOBILE_MUSIC_STORY_LAB_URL,
        image: alluratech_cover_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>The Music & Story Lab supports quick creator workflows such as auto music generation, story scenes, covers, albums, playlists, narration, instrumental requests, listening rooms, and YouTube publishing preparation.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Albums Become Playlists",
        description:
          "AlluraXR albums can map naturally to playlist structures when publishing or packaging content for external channels.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Admin and Creator Workflows",
        description:
          "Admin auto music, instrumental requests, publication review, user management, and creator-facing labs can share the same backend persistence model.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Create music and story collections",
        ctaText: "Open Mobile Lab",
        ctaLink: MOBILE_MUSIC_STORY_LAB_URL,
      },
    ],
  },
  {
    id: "xr-experience-hub",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "XR Experience Hub",
        subtitle:
          "WebXR, mobile AR, headset experiences, spatial media scenes, and guided immersive campaigns.",
        ctaText: "Open XR Dashboard",
        ctaLink: alluraXrPath("/dashboard/xr-experience"),
        image: Software_Development_Solutions_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>XR Experience Hub is the service path for immersive AlluraXR work. It can package headset and mobile AR experiences, spatial playlists, object groups, room-based scenes, and guided controls for campaigns, venues, education, and creator showcases.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "AR and VR Ready Workflows",
        description:
          "Separate mobile AR, headset VR, and WebXR interaction needs while keeping media and object placement data reusable.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Scene and Object Services",
        description:
          "Support spatial layouts, grouped objects, scene templates, teleport points, and professional positioning as service data.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Scope an XR campaign",
        ctaText: "Book Discovery",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "alluraxr-marketplaces",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "AlluraXR Marketplaces",
        subtitle:
          "Company and creator discovery surfaces connected to AlluraXR identity, profiles, programs, and service requests.",
        ctaText: "Open Profile Marketplace",
        ctaLink: alluraXrPath("/profile-marketplace"),
        image: inovation_and_collaboration_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>The marketplace surfaces help make AlluraXR users, companies, creators, and service providers discoverable. They can support regional programs, creator networks, B2B service discovery, and public-facing organization profiles.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Profile and Company Discovery",
        description:
          "Let users and organizations present themselves in a shared ecosystem instead of isolated one-off pages.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Program Visibility",
        description:
          "Regional programs can reuse marketplace data for participant discovery, service matching, campaign visibility, and collaboration.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Make services discoverable",
        ctaText: "Open Company Marketplace",
        ctaLink: alluraXrPath("/company-marketplace"),
      },
    ],
  },
  {
    id: "fullstack-craft-wizard",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Fullstack-Craft-Wizard Generator",
        subtitle:
          "Request a web or mobile service, preview the generated project in a sandbox, then download the zipped code package.",
        ctaText: "Open Sandbox",
        ctaLink: "/solutions/fullstack-craft-wizard/sandbox",
        image: backendCraftWizard_img,
      },
      {
        type: "TextSection",
        title: "Service Workflow",
        id: "overview",
        content: `
          <p>Fullstack-Craft-Wizard is moving from a backend-only toolkit into a productized full-stack service generator. A user can open the sandbox, inspect a sample project, tune project type, stack, features, pages, data models, and design direction, then send the structured request to the generator pipeline for a downloadable ZIP containing starter code.</p>
          <p>During the pilot and stabilization phase, this service can remain free because it does not currently add direct delivery cost. Once the system is stable and demand is clear, it can move into paid packages or paid implementation support.</p>
          <p>The preview step matters because the user should see how the requested project will look and behave before downloading or asking for a refinement.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Request to Generated Project",
        description:
          "Collect project type, audience, pages, app features, data models, integrations, brand direction, and delivery preferences as structured input.",
        image: backendCraftWizard_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Sandbox Preview Before Download",
        description:
          "Run the generated web or mobile experience in a server-managed sandbox so the user can inspect layout, routes, and core flows before accepting the ZIP.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "FeatureSection",
        title: "ZIP Export and Service Handoff",
        description:
          "Package the generated source code, setup instructions, environment notes, and service metadata into a downloadable handoff artifact.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "CallToAction",
        title: "Turn a service request into generated code",
        ctaText: "Open Sandbox",
        ctaLink: "/solutions/fullstack-craft-wizard/sandbox",
      },
    ],
  },
  {
    id: "ai-tools-credits",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "AI Tools & Credits",
        subtitle:
          "Controlled generation tools for assets, content, and production support inside the AlluraXR dashboard.",
        ctaText: "Open AI Tools",
        ctaLink: alluraXrPath("/dashboard/tools"),
        image: itConsultancy_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>AI Tools & Credits gives AlluraTech a practical way to expose useful generation features such as logo creation, texture generation, ad copy, social posts, and assisted content workflows while keeping usage accountable.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Service-Controlled Generation",
        description:
          "Credits make AI usage easier to package for creators, admins, and B2B clients without losing cost control.",
        image: itConsultancy_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Connected Output",
        description:
          "Generated assets can feed websites, media campaigns, XR scenes, event pages, and creator publishing workflows.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Package AI tools for a service workflow",
        ctaText: "Request Service",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "website-insights-optimization",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Website Insights and Optimization",
        subtitle:
          "Analytics setup, Microsoft Clarity support, UX review, and measurable website improvement.",
        ctaText: "Request Review",
        ctaLink: "/contact",
        image: Software_Development_Solutions_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>This service helps teams understand how users behave on a live website, then turn that evidence into prioritized UX and conversion improvements. Microsoft Clarity can provide recordings, heatmaps, rage-click signals, dead-click signals, and scroll behavior without adding heavy analytics overhead.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Behavior Evidence",
        description:
          "Use session recordings, heatmaps, and UX signals to find friction points that normal analytics often hide.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Implementation Guidance",
        description:
          "Translate findings into concrete UI, copy, routing, and technical improvements that can be handled by AlluraTech or the client team.",
        image: itConsultancy_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Improve a live website with evidence",
        ctaText: "Request Review",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "analytics-capture-studio",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Analytics & Capture Studio",
        subtitle:
          "Operational reporting for AlluraXR services, campaigns, events, creator workflows, and B2B programs.",
        ctaText: "Open Analytics",
        ctaLink: alluraXrPath("/dashboard/analytics"),
        image: alluratech_cover_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>Analytics & Capture Studio is the reporting layer for service operations. It should help AlluraTech and B2B clients understand what was created, viewed, published, requested, and completed across the ecosystem.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Service Metrics",
        description:
          "Track meaningful signals for generated projects, media, events, XR scenes, creator workflows, and regional programs.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Decision-Ready Reporting",
        description:
          "Convert platform activity into clear reports for administrators, clients, partners, and program operators.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Define a reporting workflow",
        ctaText: "Request Service",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "allurapath-finland",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "AlluraPath Finland",
        subtitle:
          "Join or lead a community project, contribute with a team, and build evidence of work reviewed by AlluraTech.",
        ctaText: "Join AlluraPath",
        ctaLink: alluraXrPath("/finland/allurapath/join"),
        image: inovation_and_collaboration_img,
      },
      {
        type: "TextSection",
        title: "Program Positioning",
        id: "overview",
        content: `
          <p>AlluraPath connects people with community projects through AlluraXR accounts. Participants can request to join a recruiting team or lead a project, agree on tasks, and submit contribution evidence for review. Public recognition is opt-in and requires approved work. Participation does not promise employment, founder status, or equity.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Build with a team",
        description:
          "Choose a project, join a team or create one, and work toward a useful contribution. Team leads can set tasks and milestones and ask AlluraTech for technical support.",
        image: discover_innoWork_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Reviewable contributions",
        description:
          "Members can submit evidence for AlluraTech review. Approved work can support an optional community badge; support scope and any costs are agreed separately with the team.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Find a project or lead a team",
        ctaText: "Start on AlluraPath",
        ctaLink: alluraXrPath("/finland/allurapath/join"),
      },
    ],
  },
  {
    id: "cameroon-creator-network",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Cameroon Creator Network",
        subtitle:
          "A creator, campaign, and community network positioned as its own program while using AlluraXR accounts and media workflows.",
        ctaText: "Open Network",
        ctaLink: alluraXrPath("/cameroon/creator-network"),
        image: alluratech_cover_img,
      },
      {
        type: "TextSection",
        title: "Program Positioning",
        id: "overview",
        content: `
          <p>Cameroon Creator Network can operate as an independent AlluraTech program for creators, campaigns, local businesses, educators, and diaspora partners. AlluraXR provides the shared login, profile, media, and publishing base.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Creator and Campaign Operations",
        description:
          "Coordinate creator visibility, campaign assets, media collections, story/music workflows, and public program pages.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Shared Identity Base",
        description:
          "Avoid rebuilding account and profile systems by using AlluraXR authentication and creator profile infrastructure.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Build a creator program pilot",
        ctaText: "Request Pilot",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "connectsolve-platform",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "ConnectSolve Solutions",
        subtitle:
          "A planned collaboration track for public-sector, startup, citizen, and expert problem-solving pilots.",
        ctaText: "Request Pilot",
        ctaLink: "/contact",
        image: connectSolve_img,
      },
      {
        type: "TextSection",
        title: "Current Status",
        id: "overview",
        content: `
          <p>ConnectSolve is not being presented as a live product yet. It should be positioned as a planned high-value solution track where AlluraTech can scope a pilot with a clear use case, stakeholder model, matching rules, collaboration flow, and implementation budget.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Pilot Before Platform",
        description:
          "Start with a scoped pilot around one problem domain, partner group, or region before building broad collaboration infrastructure.",
        image: connectSolve_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Future Platform Fit",
        description:
          "When validated, ConnectSolve can reuse AlluraXR accounts, profiles, companies, events, media, and analytics instead of creating a separate stack.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Scope a ConnectSolve pilot",
        ctaText: "Discuss Use Case",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "innoWork-platform",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "InnoWork Platform",
        subtitle:
          "A planned innovation and workforce track for participants, mentors, projects, and venture-ready collaboration.",
        ctaText: "Request Pilot",
        ctaLink: "/contact",
        image: discover_innoWork_img,
      },
      {
        type: "TextSection",
        title: "Current Status",
        id: "overview",
        content: `
          <p>InnoWork should be treated as a planned program and platform concept. The right next step is to define a pilot operating model: who participates, how projects are selected, what outcomes are measured, and how AlluraXR can support profiles, evidence, media, and service requests.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Workforce and Venture Path",
        description:
          "Connect participants, mentors, projects, skills, and practical venture opportunities through a focused pilot first.",
        image: discover_innoWork_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "AlluraXR-Backed Operations",
        description:
          "Use shared accounts, profile data, events, media evidence, and reporting before investing in a separate full platform.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Design an InnoWork pilot",
        ctaText: "Discuss Use Case",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "software-development-solutions",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Software and AI Delivery",
        subtitle:
          "Hands-on web, mobile, AI automation, integration, and deployment support for generated or custom projects.",
        ctaText: "Book Discovery",
        ctaLink: "/contact",
        image: Software_Development_Solutions_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>AlluraTech can support organizations beyond generated scaffolds with implementation, integration, UX, backend development, AI automation, deployment, and maintenance. This is the practical service layer around the platform products.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Generated or Custom Starting Point",
        description:
          "Use Fullstack-Craft-Wizard output when it fits, or move directly into a custom implementation path for complex systems.",
        image: backendCraftWizard_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Production Delivery",
        description:
          "Handle architecture, implementation, testing, integrations, deployment, analytics, and ongoing improvement.",
        image: Software_Development_Solutions_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Move from request to production",
        ctaText: "Book Discovery",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "it-ai-consultancy",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "IT and AI Consultancy",
        subtitle:
          "Practical AI strategy, integration planning, automation review, and implementation guidance.",
        ctaText: "Schedule Consultation",
        ctaLink: "/contact",
        image: itConsultancy_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>AlluraTech consultancy focuses on useful adoption: where AI helps operations, what can be automated, what should remain human-reviewed, and how the solution will be maintained in production.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "AI Strategy and Workflow Review",
        description:
          "Identify automation opportunities, cost risks, data needs, compliance constraints, and integration paths.",
        image: itConsultancy_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Implementation Roadmap",
        description:
          "Turn advisory work into a realistic delivery plan using AlluraTech platform services or custom engineering.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Clarify your AI implementation path",
        ctaText: "Schedule Consultation",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "tech-recruitment-services",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Tech Recruitment Services",
        subtitle:
          "Support for connecting technology talent, project needs, and organizations inside the AlluraTech ecosystem.",
        ctaText: "Discuss Hiring Need",
        ctaLink: "/contact",
        image: techRecruitment_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>Recruitment is best positioned as a supporting service around the ecosystem rather than a standalone software product. It can connect companies, regional programs, creators, and technical delivery needs.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Talent and Project Matching",
        description:
          "Use profile, portfolio, program, and company context to support better matching conversations.",
        image: techRecruitment_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Program Support",
        description:
          "Support AlluraPath, creator networks, and B2B delivery work with practical access to relevant technical talent.",
        image: inovation_and_collaboration_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Find or organize technical talent",
        ctaText: "Contact Us",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "tech-event-organization",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "Tech Event Organization",
        subtitle:
          "Event planning and community programming connected to AlluraXR Event HQ, media, and campaign workflows.",
        ctaText: "Plan an Event",
        ctaLink: "/contact",
        image: techRecruitment_img,
      },
      {
        type: "TextSection",
        title: "Overview",
        id: "overview",
        content: `
          <p>Tech event organization is now aligned with Event HQ. The service can include planning, logistics, promotion, media capture, post-event publishing, and immersive follow-up experiences.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "From Event to Reusable Assets",
        description:
          "Capture event value through media, updates, participant profiles, stories, campaigns, and XR-ready follow-up.",
        image: techRecruitment_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Community and B2B Formats",
        description:
          "Support workshops, creator events, hackathons, training programs, demos, and local business events.",
        image: alluratech_cover_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Plan a service-backed event",
        ctaText: "Plan an Event",
        ctaLink: "/contact",
      },
    ],
  },
  {
    id: "react-business-starter",
    pageType: "solutions",
    sections: [
      {
        type: "HeroSection",
        title: "React Business Starter",
        subtitle:
          "A legacy/internal frontend foundation now folded into Site Studio and Fullstack-Craft-Wizard delivery.",
        ctaText: "View Site Studio",
        ctaLink: "/solutions/site-studio",
        image: reactBusinessStarter_img,
      },
      {
        type: "TextSection",
        title: "Current Position",
        id: "overview",
        content: `
          <p>React Business Starter should no longer be marketed as a main standalone product. Its useful pieces belong inside the Site Studio template system and the Fullstack-Craft-Wizard generator workflow.</p>
        `,
      },
      {
        type: "FeatureSection",
        title: "Template Foundation",
        description:
          "Reusable components, page configuration, and routing patterns can still support generated business websites.",
        image: reactBusinessStarter_img,
        ctaText: "",
        imagePosition: "right",
      },
      {
        type: "FeatureSection",
        title: "Productized Through Generator Services",
        description:
          "The business value is stronger when these templates are delivered through previewable website generation and service packages.",
        image: backendCraftWizard_img,
        ctaText: "",
        imagePosition: "left",
      },
      {
        type: "CallToAction",
        title: "Use the current website generation path",
        ctaText: "Open Generator Sandbox",
        ctaLink: "/solutions/fullstack-craft-wizard/sandbox",
      },
    ],
  },
];

export const appData: AppData = {
  companyName: "AlluraTech",
  navigation: [
    { name: "Home", href: "/" },
    { name: "Solutions", href: "/solutions" },
    { name: "Join a project", href: alluraXrPath("/finland/allurapath/join") },
    { name: "About", href: "/about" },
    { name: "Privacy Policy", href: "/privacy", excludeFromFooter: true },
    { name: "Terms of Service", href: "/terms", excludeFromFooter: true },
    { name: "Contact", href: "/contact" },
  ],
  pages: {
    home: {
      sections: [
        {
          type: "HeroSection",
          title: "Create something. Build your next opportunity.",
          subtitle:
            "Explore the AlluraTech products you can use today: create on AlluraXR, join an AlluraPath team, tell your story, and find tech opportunities in Finland.",
          ctaText: "Explore live products",
          ctaLink: "#alluratech-service-ecosystem",
          secondaryCtaText: "Join an AlluraPath project",
          secondaryCtaLink: alluraXrPath("/finland/allurapath/join"),
          image: alluratechEcosystemImg,
        },
        {
          type: "ServiceCatalogSection",
          id: "alluratech-service-ecosystem",
          title: "Start with what is live",
          subtitle:
            "Five active destinations for creators, builders, and employers. Choose what you want to do and go straight to the product.",
          groups: alluraTechServiceCatalogGroups,
        },
        {
          type: "MatchXREventSection",
        },
        {
          type: "FeatureSection",
          title: "One connected place for creation",
          description:
            "AlluraXR brings creator profiles, media, and immersive experiences together. Music & Story Lab and the free video starter give people focused ways to begin.",
          image: alluratechCreatorsImg,
          ctaText: "Open AlluraXR",
          ctaLink: ALLURAXR_PRODUCTION_URL,
          imagePosition: "right",
          id: "alluraxr-production-platform",
        },
        {
          type: "FeatureSection",
          title: "Turn effort into visible work",
          description:
            "AlluraPath gives builders a team and a project to contribute to. Helsinki Dev Jobs helps them discover roles and connect skills to real opportunities.",
          image: alluratechBuildersImg,
          ctaText: "Join AlluraPath",
          ctaLink: alluraXrPath("/finland/allurapath/join"),
          imagePosition: "left",
        },
        {
          type: "CallToAction",
          title: "Want to work with AlluraTech?",
          ctaText: "Contact Us",
          ctaLink: "/contact",
        },
      ],
    },
    pricing: {
      sections: [
        {
          type: "HeroSection",
          title: "Pricing is scoped by service readiness",
          subtitle:
            "Live services, free pilots, generated projects, and custom B2B delivery need different pricing models.",
          ctaText: "Request Quote",
          ctaLink: "/contact",
          image: alluratech_cover_img,
        },
        {
          type: "TextSection",
          title: "How Pricing Works",
          id: "pricing-model",
          content: `
            <p>AlluraTech pricing should be scoped by the service path: live platform access, free pilots, generated project packages, custom implementation, or ongoing program support. Fullstack-Craft-Wizard can remain free during the pilot and stabilization phase, then move to paid packages or paid implementation support when the system is stable.</p>
          `,
        },
        {
          type: "CallToAction",
          title: "Get pricing for your use case",
          ctaText: "Request Quote",
          ctaLink: "/contact",
        },
      ],
    },
    faq: {
      sections: [
        {
          type: "HeroSection",
          title: "Frequently Asked Questions",
          subtitle:
            "Clear answers about AlluraTech services, AlluraXR, generated apps, regional programs, and planned pilots.",
          ctaText: "Read FAQ",
          ctaLink: "#full-faq",
          image: alluratech_cover_img,
        },
        {
          type: "FAQ",
          title: "Frequently Asked Questions",
          subtitle: "How the AlluraTech ecosystem is organized",
          id: "full-faq",
          faqs: [
            {
              question: "What is the current AlluraTech service ecosystem?",
              answer: `
                <p>AlluraTech is organizing its work into four groups: live AlluraXR services, full-stack and automation services, regional program platforms, and planned high-value B2B tracks.</p>
              `,
            },
            {
              question: "What is AlluraXR in this structure?",
              answer: `
                <p>AlluraXR is the shared production base for accounts, profiles, companies, media, dashboards, marketplaces, AI tools, and XR workflows. Independent services can point to their own public pages while still relying on AlluraXR infrastructure.</p>
              `,
            },
            {
              question: "How does Fullstack-Craft-Wizard work as a service?",
              answer: `
                <p>A user submits a web or mobile service request. The server generates the project, runs it in a sandbox preview so the user can inspect the result, then returns a downloadable ZIP containing the generated code and setup information. For now, this can remain a free pilot service while the system stabilizes.</p>
              `,
            },
            {
              question: "Can generated projects be previewed before download?",
              answer: `
                <p>Yes. The intended product flow is request, generate, preview in sandbox, refine if needed, then download the ZIP. This is important for both web and mobile projects because the user should see the generated experience before accepting the package.</p>
              `,
            },
            {
              question: "Will Fullstack-Craft-Wizard stay free?",
              answer: `
                <p>For now it can be free because the generator does not create direct service cost. When the workflow is stable and demand is validated, AlluraTech can introduce paid packages, premium generation limits, implementation support, or managed deployment services.</p>
              `,
            },
            {
              question: "Is React Business Starter still a product?",
              answer: `
                <p>React Business Starter should be treated as a legacy/internal template foundation. Its value is now folded into Site Studio and Fullstack-Craft-Wizard instead of being marketed as a separate main product.</p>
              `,
            },
            {
              question: "Are AlluraPath Finland and Cameroon Creator Network independent services?",
              answer: `
                <p>Yes. They can be positioned as independent AlluraTech program brands, but they should rely on AlluraXR authentication, profiles, media, dashboards, and service infrastructure where possible.</p>
              `,
            },
            {
              question: "Is ConnectSolve implemented?",
              answer: `
                <p>ConnectSolve should be shown as a planned or pilot-discussion track, not as a live platform. The right call to action is to discuss a use case or request a pilot.</p>
              `,
            },
            {
              question: "What B2B services can AlluraTech sell now?",
              answer: `
                <p>Strong near-term offers include Event HQ, Site Studio packages, Media Studio workflows, Music & Story Lab services, XR Experience Hub pilots, Fullstack-Craft-Wizard generated project packages, AI tools and credits, analytics capture, and website optimization.</p>
              `,
            },
          ],
        },
        {
          type: "CallToAction",
          title: "Need a service scoped?",
          ctaText: "Contact Us",
          ctaLink: "/contact",
        },
      ],
    },
    contact: {
      sections: [
        {
          type: "ContactInfo",
          address: "Kerava, Finland",
          email: "marius.monkam@alluratech.fi",
          whatsappCommunity: WHATSAPP_COMMUNITY_URL,
          socialLinks: alluraTechSocialLinks,
        },
      ],
    },
    blog: {
      sections: [
        {
          type: "BlogPreview",
          posts: [
            {
              title: "From Platform Modules to Service Lines",
              excerpt:
                "How AlluraTech can turn AlluraXR surfaces into clear B2B offers.",
              date: "2026-06-07",
              author: "AlluraTech",
              image:
                "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4",
            },
            {
              title: "Why Generated Apps Need Sandbox Preview",
              excerpt:
                "A better Fullstack-Craft-Wizard workflow lets users review the generated project before downloading code.",
              date: "2026-06-07",
              author: "AlluraTech",
              image:
                "https://images.unsplash.com/photo-1522202176988-66273c2fd55f",
            },
          ],
        },
      ],
    },
    solutions: {
      sections: [
        {
          type: "HeroSection",
          title: "Explore AlluraTech products",
          subtitle:
            "A focused set of active products for creating, sharing, building experience, and finding opportunities.",
          ctaText: "Browse products",
          ctaLink: "#alluratech-service-catalog",
          image: alluratechEcosystemImg,
        },
        {
          type: "ServiceCatalogSection",
          id: "alluratech-service-catalog",
          title: "Choose your starting point",
          subtitle:
            "Every destination listed here is active. Follow the product link to get started.",
          groups: alluraTechServiceCatalogGroups,
        },
        {
          type: "FeatureSection",
          title: "Built to work together",
          description:
            "Start a creative project in AlluraXR, build a team on AlluraPath, and use Helsinki Dev Jobs to discover relevant opportunities.",
          image: techRecruitment_img,
          ctaText: "See Helsinki Dev Jobs",
          ctaLink: HELSINKI_DEV_JOBS_URL,
          imagePosition: "right",
        },
        {
          type: "CallToAction",
          title: "Need help choosing a path?",
          ctaText: "Contact Us",
          ctaLink: "/contact",
        },
      ],
    },
    about: {
      hero: {
        type: "HeroSection",
        title: "About AlluraTech",
        subtitle:
          "AlluraTech connects creative tools, practical projects, and tech opportunities through active products.",
        ctaText: "Read Mission",
        ctaLink: "#our-mission",
        image: alluratech_cover_img,
      },
      mission: {
        title: "Our Mission",
        description:
          "Our work helps people create, contribute, and grow. AlluraXR supports media and immersive experiences; AlluraPath makes real project work visible; Helsinki Dev Jobs connects skills with opportunities in Finland.",
        id: "our-mission",
      },
      team: [
        {
          name: "Marius Monkam",
          role: "Founder & CEO",
          image: marius_monkam_img,
        },
      ],
      callToAction: {
        type: "CallToAction",
        title: "Collaborate with AlluraTech",
        ctaText: "Send a Request",
        ctaLink: "/contact",
      },
    },
    privacy: {
      sections: [
        {
          type: "HeroSection",
          title: "Privacy Policy",
          subtitle: "How AlluraTech handles data across its services.",
          ctaText: "Read Policy",
          ctaLink: "#introduction",
          image: alluratech_cover_img,
        },
        {
          type: "TextSection",
          title: "Introduction",
          id: "introduction",
          content: `
            <p>AlluraTech services may involve account data, contact requests, project participation, media workflows, and analytics. We collect information needed to provide and improve these services.</p>
          `,
        },
        {
          type: "TextSection",
          title: "Information We Collect",
          content: `
            <p>We may collect information provided through contact forms, service requests, account workflows, project participation, and platform usage.</p>
          `,
        },
        {
          type: "TextSection",
          title: "How We Use Information",
          content: `
            <p>We use collected information to respond to requests, operate services, improve user experience, support platform security, and communicate about relevant service work.</p>
          `,
        },
        {
          type: "CallToAction",
          title: "Have privacy questions?",
          ctaText: "Contact Us",
          ctaLink: "/contact",
        },
      ],
    },
    terms: {
      sections: [
        {
          type: "HeroSection",
          title: "Terms of Service",
          subtitle: "Terms for using AlluraTech websites and service workflows.",
          ctaText: "Read Terms",
          ctaLink: "#introduction",
          image: alluratech_cover_img,
        },
        {
          type: "TextSection",
          title: "Introduction",
          id: "introduction",
          content: `
            <p>These terms describe the general conditions for using AlluraTech websites, contact workflows, AlluraXR-linked services, and project pages.</p>
          `,
        },
        {
          type: "TextSection",
          title: "Service Requests",
          content: `
            <p>Service requests are reviewed according to the scope of the relevant product or project. Any proposed deliverables and support are agreed separately with the requester.</p>
          `,
        },
        {
          type: "TextSection",
          title: "Use of Services",
          content: `
            <p>You agree to use AlluraTech services lawfully and to provide accurate information when submitting service requests or project applications.</p>
          `,
        },
        {
          type: "CallToAction",
          title: "Need assistance?",
          ctaText: "Contact Support",
          ctaLink: "/contact",
        },
      ],
    },
    solutionDetails: alluraTechSolutionDetails.filter(({ id }) =>
      ["alluraxr-platform", "music-story-lab", "allurapath-finland"].includes(id)
    ),
  },
  footer: {
    copyWright: "AlluraTech. All rights reserved.",
    companyLinks: [
      { name: "Home", href: "/" },
      { name: "Solutions", href: "/solutions" },
      { name: "About Us", href: "/about" },
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms Of Service", href: "/terms" },
      { name: "Contact", href: "/contact" },
    ],
    socialLinks: alluraTechSocialLinks,
    integrationsLinks: [],
    legalLinks: [
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms Of Service", href: "/terms" },
    ],
  },
  theme: {
    "background-color": "#ffffff",
    "body-background-color": "#ffffff",
    "text-color": "#333333",
    "cta-background-color": "#0066cc",
    "cta-text-color": "#ffffff",
    "cta-button-background-color": "#ffffff",
    "cta-button-text-color": "#0066cc",
    "cta-button-hover-background-color": "#f0f0f0",
    "contact-form-background-color": "#ffffff",
    "submit-button-background-color": "#0066cc",
    "submit-button-text-color": "#ffffff",
    "submit-button-hover-background-color": "#004080",
    "feature-heading-color": "#333333",
    "feature-text-color": "#666666",
    "footer-background-color": "#202020",
    "footer-text-color": "#cccccc",
    "footer-link-color": "#555555",
    "footer-icon-color": "#cccccc",
    "header-background-color": "#004080",
    "header-text-color": "#ffffff",
    "header-link-color": "#ffffff",
    "header-link-hover-color": "#dddddd",
    "hero-background-color": "#0066cc",
    "hero-text-color": "#ffffff",
  },
};
